import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  type ChartData,
  type ChartOptions,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { InfoTooltip } from "./InfoTooltip";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const BUSINESS_COLORS: Record<string, string> = {
  "Audit & Assurance": "#48AB34",
  "G&P": "#E3C21E",
  "Tax": "#72C0B3",
  "People": "#A899F2",
  "Consulting Services": "#B33473",
  "GPS": "#0CAAC9",
  "Other": "#575757",
};

export interface OccupancyByBusinessPeriod {
  period: number;
  values: Record<string, number>;
}

interface OccupancyByBusinessChartProps {
  title?: string;
  tooltipText?: string;
  items: OccupancyByBusinessPeriod[];
}

export function OccupancyByBusinessChart({
  title = "DU Occupancy by Period by Sponsoring Business",
  tooltipText,
  items,
}: OccupancyByBusinessChartProps) {
  const labels = items.map((item) => `Period ${item.period}`);
  const businesses = Object.keys(BUSINESS_COLORS);

  const data: ChartData<"bar", number[], string> = {
    labels,
    datasets: businesses.map((business) => ({
      label: business,
      data: items.map((item) => item.values[business] ?? 0),
      backgroundColor: BUSINESS_COLORS[business],
      barThickness: 10,
      maxBarThickness: 12,
      categoryPercentage: 0.55,
      barPercentage: 0.55,
    })),
  };

  const options: ChartOptions<"bar"> = {
    responsive: true,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      datalabels: {
        display: false,
      },
      legend: {
        position: "bottom",
        align: "start",
        labels: {
          boxWidth: 12,
          boxHeight: 12,
          padding: 16,
          color: "#000",
        },
      },
      title: {
        display: false,
        text: title,
        align: "center",
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.dataset.label ?? "";
            const value = context.parsed.y ?? 0;
            return `${label}: ${value.toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        offset: true,
        grid: {
          display: false,
        },
       
      },
      y: {
        stacked: true,
        beginAtZero: true,
        min: 0,
        max: 35000,
        grid: {
          display: false,
        },
        ticks: {
          stepSize: 5000,
          color: "#000000",
        },
        title: {
          display: true,
          text: "Occupancy",
          color: "#000000",
        },
      },
    },
  };

  return (
    <div className="chart-box">
      <header className="box-header">
        <div className="box-title">
          <span>{title}</span>
          {tooltipText && <InfoTooltip text={tooltipText} />}
        </div>
      </header>
      <Bar data={data} options={options} />
    </div>
  );
}
